/**
 * 资产预览浮层（角色 / 物品 / 场景共用）
 *
 * 触发：picker 列表项右侧的放大镜按钮
 * 内容：大图 + 名称 + 描述全文 + 创建 / 更新时间
 * 关闭：点击外部、按 Esc、点击右上角 X
 *
 * 仅供"查看"，不允许编辑。
 */

import { useEffect } from 'react';
import { X, Calendar } from 'lucide-react';
import { withAuthToken } from '../../../../utils/storageUrl';
import { formatBeijingDateTime } from '../../../../utils/time';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 大图 URL（缺省时显示首字母彩底圆作占位） */
  imageUrl: string | null;
  /** 资产名称（标题） */
  name: string;
  /** 描述文本（可选，可多行） */
  description?: string | null;
  /** 创建时间（ISO 字符串），可选 */
  createdAt?: string | null;
  /** 更新时间（ISO 字符串），可选 */
  updatedAt?: string | null;
  /** 资产类型，决定占位形状 / 配色（character=圆 sky；prop=圆角 amber；scene=圆角 emerald） */
  kind: 'character' | 'prop' | 'scene';
}

export function AssetPreviewPopover({
  open,
  onClose,
  imageUrl,
  name,
  description,
  createdAt,
  updatedAt,
  kind,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const placeholderClass =
    kind === 'character'
      ? 'bg-gradient-to-br from-sky-100 to-sky-200 text-sky-700'
      : kind === 'prop'
        ? 'bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700'
        : 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700';

  const initialChar = name.trim().slice(0, 1) || '?';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部条 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="truncate text-base font-semibold text-slate-900">{name}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 大图 */}
        <div className="relative bg-slate-50">
          {imageUrl ? (
            <img
              src={withAuthToken(imageUrl)}
              alt={name}
              className="block max-h-[55vh] w-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className={`flex aspect-video w-full items-center justify-center text-7xl font-bold ${placeholderClass}`}>
              {initialChar}
            </div>
          )}
        </div>

        {/* 描述 + 时间 */}
        <div className="space-y-3 overflow-y-auto bg-slate-50 px-5 py-4">
          {description ? (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                描述
              </div>
              <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                {description}
              </p>
            </div>
          ) : (
            <p className="text-xs italic text-slate-400">（无描述）</p>
          )}

          {(createdAt || updatedAt) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-200 pt-3 text-[11px] text-slate-500">
              {createdAt && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  创建于 {formatBeijingDateTime(createdAt)}
                </span>
              )}
              {updatedAt && updatedAt !== createdAt && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  更新于 {formatBeijingDateTime(updatedAt)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
